import { LinearProgress } from '@mui/material';
import { DataGrid, GridValidRowModel } from '@mui/x-data-grid';
import { observer } from 'mobx-react-lite';
import { API } from '~/api';
import { DashboardService } from '~/service/service.dashboard';
import { DashboardActivitiesService } from '~/service/service.dashboard.activities';
import { UIService } from '~/service/service.ui';
import { useService } from '~/shared/service/service.base';
import {
	DashboardActivitiesToolbar,
	DashboardActivityAction,
	DashboardActivityType,
} from '~/view/dashboard/dashboard.activity';
import { DashboardHeader } from '~/view/dashboard/dashboard.header';
import { DateTimeColumn, GridColumn, useGridColumns } from '~/view/grid/grid.columns';
import { GridContainer } from '~/view/grid/grid.container';
import { GridNoRows } from '~/view/grid/grid.norows';
import { MuiGridPagination } from '~/view/grid/grid.pagination';
import { Col } from '~/view/view.box';

function UserColumn<T extends GridValidRowModel>(def: GridColumn<T>): GridColumn<T> {
	return {
		width: 200,
		filterable: false,
		renderCell: ({ value }) => {
			return value ? value.nickName : 'DELETED';
		},
		...def,
	};
}

function TypeColumn<T extends GridValidRowModel>(def: GridColumn<T>): GridColumn<T> {
	return {
		width: 200,
		filterable: false,
		renderCell: ({ row }) => <DashboardActivityType activity={row} />,
		minWidth: 300,
		...def,
	};
}

function ActionColumn<T extends GridValidRowModel>(def: GridColumn<T>): GridColumn<T> {
	return {
		width: 150,
		filterable: false,
		renderCell: ({ row }) => <DashboardActivityAction activity={row} />,
		...def,
	};
}

export const DashboardActivitiesPage = observer(() => {
	const [ui, dashSvc, svc] = useService(UIService, DashboardService, DashboardActivitiesService);
	ui.usePageTitle('Dashboard');

	svc.useSearchParams();
	svc.useActivitiesList();
	dashSvc.useActivities();

	const columns = useGridColumns<API.AnalyticsListItem>(() => [
		DateTimeColumn({ field: 'createdAt', headerName: 'Date time', minWidth: 200 }),
		UserColumn({ field: 'user', headerName: 'User', minWidth: 200, sortable: false }),
		TypeColumn({ field: 'type', headerName: 'Type', sortable: false }),
		ActionColumn({ field: 'action', headerName: 'Action', sortable: false }),
		{ field: 'value', headerName: 'Value', flex: 1, sortable: false },
	]);

	return (
		<GridContainer async={svc.async}>
			<Col pb={1} justifyContent="flex-start" py={2}>
				<DashboardHeader />
			</Col>
			<DataGrid
				initialState={svc.gridState.initial()}
				loading={svc.async.loading}
				rowCount={svc.totalCount}
				rows={svc.items.slice()}
				columns={columns}
				paginationMode="server"
				sortingMode="server"
				disableColumnFilter
				onStateChange={svc.gridState.onStateChange}
				slots={{
					pagination: MuiGridPagination,
					loadingOverlay: LinearProgress,
					toolbar: () => <DashboardActivitiesToolbar />,
					noRowsOverlay: () => <GridNoRows>No actions found</GridNoRows>,
				}}
			/>
		</GridContainer>
	);
});
